import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Global404, Route404 } from '@saviynt/common';
import { OverlayProvider } from '@saviynt/design-system';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IntlProvider } from 'react-intl';
import { useSelector } from 'react-redux';
import { BrowserRouter as Router, Route } from 'react-router-dom';

import { ManageClassification } from '../components';
import { ExtProvider } from '../context';
import { ProviderSettings } from '../features';
import IDPProviders from '../features/IDProofing/components/IDPProviders/IDPProviders';
import { type RootState } from '../services/types';
import REQUEST_BASE_URL from '../utils/constants';
import {
  hideHostAppElements,
  hideHostAppElementsCleanup,
  removeDynamicBodyClasses,
} from '../utils/hideHostElements';

import Dashboard from './Dashboard/Dashboard';
import UserManagement from './UserManagement/UserManagement';

export const USERMANAGEMENT_BASE_URL = '/ECMv6/usermanagement';

const queryClient = new QueryClient();

function Routes({ basename = REQUEST_BASE_URL }: { basename?: string }) {
  const initialAllowedRoutes = useMemo(
    () => ({
      staticRoutes: [
        REQUEST_BASE_URL,
        `/dashboard`,
        `/manageClassification`,
        `/idproofing/providers`,
        `/idproofing/providers/settings`,
        `/`,
      ],
      dynamicRoutes: [],
      blockedUrlList: [],
      blockedNames: [],
    }),
    []
  );

  const externalClassificationInfo = useSelector(
    (state: RootState) => state.user.externalClassificationInfo
  );

  const isIDProofingEnabled = useSelector(
    (state: RootState) => state.user.isIDProofingEnabled
  );

  const { messages } = useSelector((state: RootState) => state.user);

  const [allowedRoutes, setAllowedRoutes] = useState(initialAllowedRoutes);

  useEffect(() => {
    /* hide host app elements not needed for Gen2 UI */
    hideHostAppElements();
    removeDynamicBodyClasses();

    return () => {
      hideHostAppElementsCleanup();
    };
  }, []);

  const isExtClassificationEnabled =
    externalClassificationInfo.externalClassification === '1';

  useEffect(() => {
    const updatedAllowedRoutes = { ...initialAllowedRoutes };

    if (!isExtClassificationEnabled) {
      updatedAllowedRoutes.staticRoutes =
        updatedAllowedRoutes.staticRoutes.filter(
          (route) => route !== `/dashboard` && route !== `/manageClassification`
        );
    }

    if (!isIDProofingEnabled) {
      updatedAllowedRoutes.staticRoutes =
        updatedAllowedRoutes.staticRoutes.filter(
          (route) =>
            route !== `/idproofing/providers` &&
            route !== `/idproofing/providers/settings`
        );
    }

    setAllowedRoutes(updatedAllowedRoutes);
  }, [isExtClassificationEnabled, isIDProofingEnabled, initialAllowedRoutes]);

  return (
    <ExtProvider>
      <QueryClientProvider client={queryClient}>
        <Suspense key='Ext' fallback={<>test</>}>
          <IntlProvider locale='en' messages={messages}>
            <OverlayProvider>
              <Router basename={basename}>
                {/* @ts-expect-error - TODO: Sireesha ptal */}
                <Route404 routes={allowedRoutes} render={() => <Global404 />}>
                  <Route exact path='/dashboard' component={Dashboard} />
                  <Route
                    exact
                    path='/manageClassification'
                    component={ManageClassification}
                  />
                  <Route
                    exact
                    path='/idproofing/providers'
                    component={IDPProviders}
                  />
                  <Route
                    exact
                    path='/idproofing/providers/settings'
                    component={ProviderSettings}
                  />
                  <Route exact path='/' component={UserManagement} />
                </Route404>
              </Router>
            </OverlayProvider>
          </IntlProvider>
        </Suspense>
      </QueryClientProvider>
    </ExtProvider>
  );
}

Routes.defaultProps = { basename: REQUEST_BASE_URL };

export default Routes;
